import { useMemo } from 'react';
import { BROWSER_LANGUAGE } from '@gp/constants/const';
import { getNullTextOfFilter, unitConversion } from '@/pages/datalab/utils/utils';
import { useIntl } from 'umi';
import Decimal from 'decimal.js';
import { LANGUAGE_EN } from '@home/_config/config';
import type { ILineChartProps } from './types';
import {
  getDatalabTooltipFormatter,
  getTooltipConfig,
} from '@customComp/Chart/chartConfigs/tooltip';
import { getSeriesAndLegend, setAloneSymbolOpacity } from "@customComp/Chart/chartConfigs/seriesUtils";
import { applyNullUiToLineSeries } from '@customComp/Chart/chartConfigs/nullHandling';
import { getLegendConfig } from "@customComp/Chart/chartConfigs/legend/legendUtils";
import { ChartOption } from '../_config/_interface';

export const useLineOption = (props: ILineChartProps) => {
  const {
    xAxisData,
    seriesData,
    metricIsAllPercent,
    metricNames,
    compareDateCount,
    isOnlyOneDimension,
    fieldInfos,
    chartConfig,
    userFilterControls,
    tooltipMinTopPx,
    setOtherConfigFunc
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 单独计算 option其他固定配置
  const option = useMemo(() => {
    const displayConfig: Record<string, { accuracy: number; unit: string; }> = {};
    metricNames.forEach((metricName: string) => {
      const { accuracy, unit } = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || { accuracy: 0, unit: 'bmk' };
      displayConfig[metricName] = { accuracy, unit };
    });
    const { series: rawSeries, legendData } = getSeriesAndLegend({
      seriesData,
      setSeriesItem: (seriesItem, { isCompare, color }) => {

        const { valueType, data } = seriesItem || {};
        let newData;
        if (!metricIsAllPercent && valueType === 'percent') {
          // 当有多个指标, 且不全是百分比类型时, 那么百分比类型的数据要除以100, 用于适配Y轴的数值

          newData = data?.map(item => ({
            ...item,
            value: item?.value ? Decimal.div(Number(item?.value) || 0, 100).toString() : item?.value,
            isDecimalPercent: true,
          }));
        }
        return {
          ...seriesItem,
          data: newData || data,
          type: 'line',
          lineStyle: {
            type: isCompare ? 'dashed' : 'solid', // 对比时间折线显示虚线
            color,
          },
          itemStyle: {
            opacity: 0,
            color,
          },
          symbol: 'circle',
        };
      },
      isEn,
      displayConfig,
      compareDateCount,
      legendColors: chartConfig?.legendColors,
    });
    const nullUi = chartConfig?.nullUi;
    const series = rawSeries.map((seriesItem) =>
      applyNullUiToLineSeries(seriesItem, nullUi),
    );
    // ignore 模式下 connectNulls 全线连接，跳过 setAloneSymbolOpacity：
    // 若仍执行孤立点标记，折线上会出现「部分圆点显示、部分不显示」的混杂效果
    // （仅原孤立点 opacity=1，连续线段上的点仍 opacity=0），与 ignore 仅展示折线的意图不符。
    if (nullUi !== 'ignore') {
      // 判断是否是单独的点, 给单独的点设置透明度为1 显示出来
      setAloneSymbolOpacity(series);
    }
    // 设置y轴配置
    const baseYAxis = {
      type: 'value',
      splitLine: {
        // 分割线效果
        show: true,
        lineStyle: {
          type: 'dashed',
          color: '#ddd',
        },
      },
      axisLabel: {
        color: '#838697',
        fontSize: 12,
        formatter(value: number) {
          if (metricIsAllPercent) { // 如果指标都是百分比类型, 那么Y轴展示百分比
            return `${value}%`;
          }
          return unitConversion(value, true);
        }
      },
    };
    // 判断是否是双y轴
    const isDualYAxis = series?.some(seriesItem => seriesItem?.yAxisIndex === 1);
    const yAxis = isDualYAxis ? [baseYAxis, baseYAxis] : [baseYAxis];
    return {
      grid: {
        left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
        top: 22,
        right: 4,
        bottom: 25,
        containLabel: true
      },
      legend: getLegendConfig({ data: legendData }),
      xAxis: {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          // 不用写入textStyle对象里面，会echart报黄色提示
          color: '#838697',
          fontSize: 10,
          align: 'center',
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true,
        },
        data: (xAxisData ?? []).map(text => getNullTextOfFilter(text)),
      },
      tooltip: getTooltipConfig({
        tooltipMinTopPx,
        className: userFilterControls?.length > 1 ? 'dbl-chart-tooltip-user-filter' : '',
        formatter: getDatalabTooltipFormatter({
          metricNames,
          compareDateCount,
          isOnlyOneDimension,
          userFilterControls,
          stackedDynamicState: chartConfig?.stackedDynamicState !== false,
        }),
      }),
      yAxis,
      series,
    };
  }, [
    xAxisData,
    seriesData,
    isEn,
    fieldInfos,
    tooltipMinTopPx,
    // compareDateCount,
    chartConfig?.legendColors,
    chartConfig?.nullUi,
  ]);

  return useMemo(() => setOtherConfigFunc ? setOtherConfigFunc(option as ChartOption) : option, [option, setOtherConfigFunc]);
};